import {
  MIXPANEL_EVENTS,
  type MixpanelEventName,
} from '@savvy/mixpanel-events';
import mixpanel from 'mixpanel-browser';

export type { MixpanelEventName };
export { MIXPANEL_EVENTS };

export type MixpanelUserIdentity = {
  email: string;
  firstName: string | null;
  id: number;
  lastName: string | null;
  role: string;
};

export type MixpanelAuthenticatedUser = MixpanelUserIdentity & {
  account: { id: string; name: string };
};

export const MIXPANEL_EVENT_NAMES = {
  ADD_COMPANY_SEARCHED: MIXPANEL_EVENTS.search.addCompanySearched,
  COMPANY_ADDED_FROM_SEARCH: MIXPANEL_EVENTS.search.companyAddedFromSearch,
  ERROR: MIXPANEL_EVENTS.error.tracked,
  PROJECT_OPENED: MIXPANEL_EVENTS.project.opened,
  SEARCH: MIXPANEL_EVENTS.search.performed,
  SEARCH_ADD_FLOW_OPENED: MIXPANEL_EVENTS.search.addFlowOpened,
  SEARCH_RESULT_CLICKED: MIXPANEL_EVENTS.search.resultClicked,
  SIGN_IN: MIXPANEL_EVENTS.auth.signIn,
  SPOTLIGHT_CARD_ACTION: MIXPANEL_EVENTS.spotlight.cardAction,
  SPOTLIGHT_CONTACT_FORM_ABANDONED:
    MIXPANEL_EVENTS.spotlight.contactFormAbandoned,
  SPOTLIGHT_CONTACT_FORM_STARTED: MIXPANEL_EVENTS.spotlight.contactFormStarted,
  SPOTLIGHT_CONTACT_FORM_SUBMITTED:
    MIXPANEL_EVENTS.spotlight.contactFormSubmitted,
  SPOTLIGHT_PAGE_EXITED: MIXPANEL_EVENTS.spotlight.pageExited,
  SPOTLIGHT_PAGE_VIEWED: MIXPANEL_EVENTS.spotlight.pageViewed,
  SPOTLIGHT_SEARCH: MIXPANEL_EVENTS.spotlight.search,
} as const;

function getMixpanelToken(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const { VITE_MIXPANEL_KEY, VITE_MIXPANEL_TOKEN } = window.runTimeEnvs;

  return VITE_MIXPANEL_KEY || VITE_MIXPANEL_TOKEN || '';
}

let isMixpanelInitialized = false;

let isSessionRecordingActive = false;

let accountContext: {
  accountId: string;
  accountName: string;
} | null = null;

export function initMixpanel(): void {
  if (isMixpanelInitialized) {
    return;
  }

  const token = getMixpanelToken();
  if (!token) {
    return;
  }

  try {
    mixpanel.init(token, {
      autocapture: true,
      debug: import.meta.env.DEV,
      persistence: 'localStorage',
      record_block_class: 'mp-block',
      record_collect_fonts: true,
      record_mask_all_inputs: true,
      record_mask_text_class: 'mp-mask',
      record_mask_text_selector: '.mp-mask',
      record_sessions_percent: 0,
      track_pageview: 'url-with-path',
    });

    isMixpanelInitialized = true;
  } catch {
    // Mixpanel disabled or invalid token — avoid breaking the app
  }
}

function registerAccountSuperProperties(): void {
  if (accountContext === null) {
    return;
  }

  mixpanel.register({
    account_id: accountContext.accountId,
    account_name: accountContext.accountName,
  });
}

export function startSessionRecording(): void {
  if (!isMixpanelInitialized) {
    return;
  }

  try {
    registerAccountSuperProperties();
    mixpanel.start_session_recording();
    isSessionRecordingActive = true;
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function stopSessionRecording(): void {
  if (!isMixpanelInitialized || !isSessionRecordingActive) {
    return;
  }

  try {
    mixpanel.stop_session_recording();
    isSessionRecordingActive = false;
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function identifyUser(user: MixpanelUserIdentity): void {
  if (!isMixpanelInitialized) {
    return;
  }

  try {
    mixpanel.identify(String(user.id));

    const displayName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ');

    mixpanel.people.set({
      $email: user.email,
      $last_seen: new Date().toISOString(),
      ...(displayName ? { $name: displayName } : {}),
      role: user.role,
    });
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function setAccountContext({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}): void {
  if (!isMixpanelInitialized) {
    return;
  }

  try {
    accountContext = { accountId, accountName };
    registerAccountSuperProperties();

    mixpanel.people.set({
      account_id: accountId,
      account_name: accountName,
    });
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function resetMixpanel(): void {
  if (!isMixpanelInitialized) {
    return;
  }

  try {
    stopSessionRecording();
    accountContext = null;
    mixpanel.reset();
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function syncMixpanelAuthenticatedUser(
  user: MixpanelAuthenticatedUser,
): void {
  identifyUser(user);
  setAccountContext({
    accountId: user.account.id,
    accountName: user.account.name,
  });
  startSessionRecording();
}

export function trackEvent(
  eventName: MixpanelEventName,
  properties?: Record<string, unknown>,
): void {
  if (!isMixpanelInitialized) {
    return;
  }

  try {
    mixpanel.track(eventName, properties);
  } catch {
    // No-op if Mixpanel is unavailable
  }
}

export function getMarketingUtmParams(): {
  utm_campaign?: string;
  utm_medium?: string;
  utm_source?: string;
} {
  if (typeof window === 'undefined') {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get('utm_source') ?? undefined;
  const utm_medium = params.get('utm_medium') ?? undefined;
  const utm_campaign = params.get('utm_campaign') ?? undefined;

  return {
    ...(utm_campaign ? { utm_campaign } : {}),
    ...(utm_medium ? { utm_medium } : {}),
    ...(utm_source ? { utm_source } : {}),
  };
}

export function trackSignIn(properties: {
  login_method: string;
  success: boolean;
  user_id?: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SIGN_IN, {
    ...properties,
    ...(properties.user_id !== undefined
      ? { distinct_id: String(properties.user_id) }
      : {}),
  });
}

export type GlobalSearchAddFlowType = 'addCompany' | 'addCompanyToProjects';

export type GlobalSearchEntityType = 'company' | 'project' | 'team';

export function trackSearch(properties: {
  active_filter: string;
  is_pagination: boolean;
  results_count: number;
  search_query: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SEARCH, properties);
}

export function trackMixpanelSearchResultClicked(properties: {
  entity_id: string;
  entity_type: GlobalSearchEntityType;
  entry_surface: string;
  is_recent: boolean;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SEARCH_RESULT_CLICKED, properties);
}

export function trackSearchAddFlowOpened(properties: {
  flow_type: GlobalSearchAddFlowType;
  project_id?: string | null;
  search_query: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SEARCH_ADD_FLOW_OPENED, properties);
}

export function trackError(properties: {
  error_code?: number;
  error_message: string;
  error_type: string;
  page_url: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.ERROR, properties);
}

export function trackAddCompanySearched(properties: {
  search_query: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.ADD_COMPANY_SEARCHED, properties);
}

export function trackCompanyAddedFromSearch(properties: {
  company_id: string;
  search_query: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.COMPANY_ADDED_FROM_SEARCH, properties);
}

export function trackProjectOpened(properties: {
  project_id: string;
  project_name: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.PROJECT_OPENED, properties);
}

export type SpotlightCardActionType =
  | 'contact_clicked'
  | 'savvy_page_clicked'
  | 'teams_expanded'
  | 'website_clicked';

export function getMixpanelSessionId(): string | null {
  if (!isMixpanelInitialized) {
    return null;
  }

  try {
    const sessionId = mixpanel.get_property('$session_id');

    return typeof sessionId === 'string' ? sessionId : null;
  } catch {
    return null;
  }
}

export function getSpotlightUserProperties(user: MixpanelUserIdentity): {
  user_id: number;
} {
  return { user_id: user.id };
}

export function trackSpotlightPageViewed(properties: {
  session_id: string | null;
  user_id: number;
  user_role: string;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_PAGE_VIEWED, {
    user_id: properties.user_id,
    user_role: properties.user_role,
    ...(properties.session_id !== null
      ? { session_id: properties.session_id }
      : {}),
  });
}

export function trackSpotlightPageExited(properties: {
  session_id: string | null;
  time_on_page_seconds: number;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_PAGE_EXITED, {
    time_on_page_seconds: properties.time_on_page_seconds,
    user_id: properties.user_id,
    ...(properties.session_id !== null
      ? { session_id: properties.session_id }
      : {}),
  });
}

export function trackSpotlightCardAction(properties: {
  action_type: SpotlightCardActionType;
  company_id: string;
  company_name: string;
  company_stage?: string;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_CARD_ACTION, {
    action_type: properties.action_type,
    company_id: properties.company_id,
    company_name: properties.company_name,
    user_id: properties.user_id,
    ...(properties.company_stage !== undefined
      ? { company_stage: properties.company_stage }
      : {}),
  });
}

export function trackSpotlightSearch(properties: {
  is_pagination: boolean;
  results_count: number;
  search_query: string;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_SEARCH, properties);
}

export function trackSpotlightContactFormStarted(properties: {
  company_id: string;
  company_name: string;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_CONTACT_FORM_STARTED, properties);
}

export function trackSpotlightContactFormAbandoned(properties: {
  company_id: string;
  company_name: string;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_CONTACT_FORM_ABANDONED, properties);
}

export function trackSpotlightContactFormSubmitted(properties: {
  company_id: string;
  company_name: string;
  user_id: number;
}): void {
  trackEvent(MIXPANEL_EVENT_NAMES.SPOTLIGHT_CONTACT_FORM_SUBMITTED, properties);
}
